import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Star, Settings, Shield, Trash2, 
  AlertTriangle, Plus, Calendar, Activity, Clock,
  FileText, Phone, Mail
} from 'lucide-react';
import api from '../../api/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { STATUS_CONFIG, getWhatsAppLink, formatPhoneDisplay } from './constants';

export const UserDetailModal = ({ user, onClose, onRefresh }) => {
  const [credits, setCredits] = useState(0);
  const [reason, setReason] = useState('');
  const [rating, setRating] = useState(user.rating || 0);
  const [plan, setPlan] = useState(user.plan);
  const [status, setStatus] = useState(user.status || 'activo');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState('soft');

  useEffect(() => {
    fetchUserDetail();
  }, [user.id]);

  const fetchUserDetail = async () => {
    try {
      const response = await api.get(`/admin/users/${user.id}`);
      setUserDetail(response.data);
      setNotes(response.data.user.notes || '');
      setStatus(response.data.user.status || 'activo');
    } catch (error) {
      toast.error('Error al cargar detalles del usuario');
    }
  };

  const handleAddCredits = async () => {
    if (credits === 0) return;
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/credits`, { credits, reason });
      toast.success(`Se otorgaron ${credits} créditos`);
      setCredits(0);
      setReason('');
      onRefresh();
      fetchUserDetail();
    } catch (error) {
      toast.error('Error al otorgar créditos');
    } finally {
      setLoading(false);
    }
  };

  const handleSetRating = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/rating`, { rating });
      toast.success('Calificación actualizada');
      onRefresh();
    } catch (error) {
      toast.error('Error al actualizar calificación');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPlan = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/plan`, { plan });
      toast.success('Plan actualizado');
      onRefresh();
    } catch (error) {
      toast.error('Error al actualizar plan');
    } finally {
      setLoading(false);
    }
  };

  const handleSetStatus = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/status`, { status });
      toast.success('Clasificación actualizada');
      onRefresh();
      fetchUserDetail();
    } catch (error) {
      toast.error('Error al actualizar clasificación');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/notes`, { notes });
      toast.success('Notas guardadas');
      fetchUserDetail();
    } catch (error) {
      toast.error('Error al guardar notas');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async () => {
    setLoading(true);
    try {
      const newIsAdmin = !user.is_admin;
      await api.post(`/admin/users/${user.id}/admin`, { is_admin: newIsAdmin });
      toast.success(user.is_admin ? 'Permisos de admin revocados' : 'Usuario promovido a admin');
      onRefresh();
      onClose(); // Cerrar modal para refrescar la lista
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar permisos');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setLoading(true);
    try {
      await api.delete(`/admin/users/${user.id}?permanent=${deleteType === 'permanent'}`);
      toast.success(deleteType === 'permanent' ? 'Usuario eliminado permanentemente' : 'Usuario marcado como eliminado');
      onRefresh();
      onClose();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreUser = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/restore`);
      toast.success('Usuario restaurado');
      onRefresh();
      fetchUserDetail();
    } catch (error) {
      toast.error('Error al restaurar usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4" onClick={onClose}>
      <div 
        className="bg-background border border-white/10 rounded-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="p-4 sm:p-6 border-b border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 sticky top-0 bg-background z-10">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
              user.status === 'eliminado' ? 'bg-red-500/20' : 'bg-primary/20'
            }`}>
              <span className={`text-lg sm:text-xl font-bold ${user.status === 'eliminado' ? 'text-red-400' : 'text-primary'}`}>
                {user.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg sm:text-xl font-bold truncate">{user.name}</h2>
                {user.is_admin && <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500 flex-shrink-0" />}
                {user.status && (
                  <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${STATUS_CONFIG[user.status]?.color || 'bg-gray-500/20'}`}>
                    {STATUS_CONFIG[user.status]?.label || user.status}
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{user.email}</p>
              {user.whatsapp_number && (
                <p className="text-xs sm:text-sm text-green-400 flex items-center gap-1 mt-1">
                  <Phone className="h-3 w-3" />
                  {formatPhoneDisplay(user.whatsapp_number)}
                </p>
              )}
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={onClose} 
            className="absolute top-3 right-3 sm:relative sm:top-auto sm:right-auto min-w-[44px] min-h-[44px] rounded-lg"
          >
            ✕
          </Button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {/* User restored button */}
          {user.status === 'eliminado' && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <span className="text-red-300 text-sm">Este usuario está marcado como eliminado</span>
              </div>
              <Button onClick={handleRestoreUser} disabled={loading} size="sm" className="w-full sm:w-auto min-h-[44px] rounded-lg">
                Restaurar Usuario
              </Button>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            {/* Tabs responsivas con scroll horizontal en móvil */}
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6">
              <TabsList className="grid w-max sm:w-full grid-cols-4 bg-white/5 min-w-[320px]">
                <TabsTrigger value="info" className="text-xs sm:text-sm min-h-[40px]">Info</TabsTrigger>
                <TabsTrigger value="bookings" className="text-xs sm:text-sm min-h-[40px]">Reservas</TabsTrigger>
                <TabsTrigger value="analysis" className="text-xs sm:text-sm min-h-[40px]">Análisis</TabsTrigger>
                <TabsTrigger value="transactions" className="text-xs sm:text-sm min-h-[40px]">Transacciones</TabsTrigger>
              </TabsList>
            </div>

            {/* Tab: Info */}
            <TabsContent value="info" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Clasificación */}
              <Card className="glass-effect border-white/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="h-5 w-5" /> Clasificación
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => setStatus(key)}
                          className={`p-3 rounded-lg border transition-all flex items-center gap-2 ${
                            status === key 
                              ? `${config.color} border-current` 
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                  <Button onClick={handleSetStatus} disabled={loading || status === (userDetail?.user?.status || 'activo')} className="w-full">
                    Actualizar Clasificación
                  </Button>
                </CardContent>
              </Card>

              {/* Otorgar créditos */}
              <Card className="glass-effect border-white/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="h-5 w-5" /> Otorgar Créditos
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input 
                    type="number" 
                    placeholder="Cantidad de créditos"
                    value={credits}
                    onChange={(e) => setCredits(parseInt(e.target.value) || 0)}
                  />
                  <Input 
                    placeholder="Motivo (opcional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <Button onClick={handleAddCredits} disabled={loading || credits === 0} className="w-full">
                    <Plus className="h-4 w-4 mr-2" /> Otorgar Créditos
                  </Button>
                </CardContent>
              </Card>

              {/* Calificación */}
              <Card className="glass-effect border-white/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Star className="h-5 w-5" /> Calificación
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setRating(star)}
                        className={`text-3xl transition-colors ${
                          star <= rating ? 'text-yellow-500' : 'text-gray-600'
                        }`}
                      >
                        ★
                      </button>
                    ))}
                    <span className="ml-2 text-sm text-muted-foreground">{rating}/5</span>
                  </div>
                  <Button onClick={handleSetRating} disabled={loading} className="w-full">
                    Guardar Calificación
                  </Button>
                </CardContent>
              </Card>

              {/* Cambiar Plan */}
              <Card className="glass-effect border-white/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="h-5 w-5" /> Plan
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <select 
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded"
                  >
                    <option value="none">Sin Plan</option>
                    <option value="inicial">Plan Inicial</option>
                    <option value="basic">Plan Basic</option>
                    <option value="socio">Plan Socio</option>
                  </select>
                  <Button onClick={handleSetPlan} disabled={loading} className="w-full">
                    Actualizar Plan
                  </Button>
                </CardContent>
              </Card>

              {/* Admin Toggle */}
              <Card className="glass-effect border-white/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5" /> Permisos de Admin
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={handleToggleAdmin} 
                    disabled={loading}
                    variant={user.is_admin ? "destructive" : "default"}
                    className="w-full"
                  >
                    {user.is_admin ? 'Revocar Admin' : 'Hacer Admin'}
                  </Button>
                </CardContent>
              </Card>

              {/* Notas */}
              <Card className="glass-effect border-white/10">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" /> Notas del Admin
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <textarea 
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Escribir notas sobre el usuario..."
                    className="w-full p-3 bg-white/5 border border-white/10 rounded min-h-[100px] resize-none"
                  />
                  <Button onClick={handleSaveNotes} disabled={loading} className="w-full">
                    Guardar Notas
                  </Button>
                </CardContent>
              </Card>

              {/* Eliminar Usuario */}
              <Card className="glass-effect border-red-500/20 md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-red-400">
                    <Trash2 className="h-5 w-5" /> Zona de Peligro
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!showDeleteConfirm ? (
                    <Button 
                      onClick={() => setShowDeleteConfirm(true)} 
                      variant="destructive"
                      className="w-full"
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Eliminar Usuario
                    </Button>
                  ) : (
                    <div className="space-y-3 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                      <p className="text-sm text-red-300 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        ¿Estás seguro de eliminar a este usuario?
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setDeleteType('soft')}
                          className={`flex-1 p-2 rounded text-sm ${
                            deleteType === 'soft' ? 'bg-yellow-500/30 border-yellow-500' : 'bg-white/5'
                          } border`}
                        >
                          Soft Delete
                          <p className="text-xs text-muted-foreground">Marcar como eliminado</p>
                        </button>
                        <button
                          onClick={() => setDeleteType('permanent')}
                          className={`flex-1 p-2 rounded text-sm ${
                            deleteType === 'permanent' ? 'bg-red-500/30 border-red-500' : 'bg-white/5'
                          } border`}
                        >
                          Permanente
                          <p className="text-xs text-muted-foreground">Eliminar todo</p>
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)} className="flex-1">
                          Cancelar
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteUser} disabled={loading} className="flex-1">
                          {deleteType === 'permanent' ? 'Eliminar Permanentemente' : 'Marcar Eliminado'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Reservas */}
            <TabsContent value="bookings">
              <Card className="glass-effect border-white/10">
                <CardHeader>
                  <CardTitle>Historial de Reservas ({userDetail?.stats?.total_bookings || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {userDetail?.bookings?.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {userDetail.bookings.map((b, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg text-sm">
                          <div>
                            <p className="font-medium">{new Date(b.date).toLocaleDateString('es-AR')} - {b.time_slot}</p>
                            <p className="text-xs text-muted-foreground">
                              Creada: {new Date(b.created_at).toLocaleDateString('es-AR')}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            b.status === 'confirmed' ? 'bg-green-500/20 text-green-300' :
                            b.status === 'cancelled' ? 'bg-red-500/20 text-red-300' :
                            'bg-yellow-500/20 text-yellow-300'
                          }`}>
                            {b.status === 'confirmed' ? 'Confirmada' : b.status === 'cancelled' ? 'Cancelada' : 'Pendiente'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No hay reservas registradas</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Análisis */}
            <TabsContent value="analysis">
              <Card className="glass-effect border-white/10">
                <CardHeader>
                  <CardTitle>Análisis de Video ({userDetail?.stats?.total_analyses || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {userDetail?.analyses?.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {userDetail.analyses.map((a, idx) => (
                        <div key={idx} className="p-3 bg-white/5 rounded-lg text-sm">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">Score: {a.score}/100</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(a.created_at).toLocaleDateString('es-AR')}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">{a.summary}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No hay análisis registrados</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Transacciones */}
            <TabsContent value="transactions">
              <Card className="glass-effect border-white/10">
                <CardHeader>
                  <CardTitle>Historial de Transacciones ({userDetail?.transactions?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {userDetail?.transactions?.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {userDetail.transactions.map((t, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg text-sm">
                          <div>
                            <p className="font-medium">{t.description}</p>
                            <p className="text-xs text-muted-foreground">{t.type}</p>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${t.amount >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {t.amount >= 0 ? '+' : ''}{t.amount} créditos
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(t.created_at).toLocaleString('es-AR')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No hay transacciones registradas</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
